<template>
  <!-- Thumbnail preview -->
  <div
    v-if="file"
    class="aspect-square tablet-h:max-w-[550px] tablet-v:max-h-[402px] tablet-h:max-h-[550px] w-full tablet-h:h-auto flex justify-center items-center border border-[#CCCCCC]/50 rounded-xl mb-2.5 relative"
    :class="{ 'cursor-pointer': hasPreviewImage }"
  >
    <Player
      v-if="showMediaPlayer"
      :url="streamUrl"
      :type="file.type"
      class="p-[9px]"
    />
    <ThumbnailIcon
      v-else
      class="w-full h-full p-[9px]"
      :class="{ 'cursor-pointer': hasPreviewImage }"
      :type="file.type"
      :icon-size="320"
      :image-url="previewImageUrl"
      :image-sizes="[IMAGE_SMALL, IMAGE_LARGE]"
      showShadow
      transition
      @click="$emit('toggleViewer')"
    />
  </div>
  <Skeleton
    v-else
    class="aspect-square tablet-h:max-w-[550px] max-h-[550px] w-full h-[402px] tablet-h:h-auto rounded-md mb-2.5"
  />

  <!-- Action buttons -->
  <div v-if="file" class="flex items-center mb-2.5">
    <UiButton
      v-if="canDownload"
      primary
      icon="Download"
      class="mr-2.5"
      @click="$emit('download')"
    >
      Download
    </UiButton>
    <UiButton icon="Lock" class="mr-auto" @click="$emit('share')">
      Share
    </UiButton>

    <template v-if="showMenu">
      <Float.Base class="hidden tablet-h:block">
        <Float.Trigger>
          <UiButton icon="ThreeDots" />
        </Float.Trigger>
        <Float.Component>
          <FileContextMenu :file="file" @clicked="$emit('contextMenuClose')" />
        </Float.Component>
      </Float.Base>
    </template>

    <UiButton
      v-if="showMenu"
      class="tablet-h:hidden"
      icon="ThreeDots"
      @click="$emit('threeDots')"
    />
  </div>
</template>

<script setup lang="ts">
import { ThumbnailIcon, Float, UiButton, Skeleton } from '@dacr/ui'
import Player from '@/features/copyright/components/Player.vue'
import FileContextMenu from '@/features/explorer/components/FileContextMenu.vue'
import { IMAGE_LARGE, IMAGE_SMALL } from '@/shared/utils/image'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { FileType } from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { computed } from 'vue'

const props = defineProps<{
  file?: CopyrightFile
  canDownload: boolean
  showMenu: boolean
  hasPrivilegedRole: boolean
  isPrivate: boolean
}>()

defineEmits<{
  toggleViewer: []
  download: []
  share: []
  threeDots: []
  contextMenuClose: []
}>()

const hasPreviewImage = computed(() => !!props.file?.asset?.previewImageUrl)

const previewImageUrl = computed(() =>
  props.file?.asset?.previewImageUrl
    ? applyAuthorizationToken(props.file.asset.previewImageUrl)
    : undefined
)

const streamUrl = computed(() => {
  const asset = props.file?.asset
  if (asset?.__typename === 'StreamAsset') return asset.streamUrl
  return undefined
})

const showMediaPlayer = computed(() => {
  if (!props.file) return false

  const isMedia =
    props.file.type === FileType.Audio || props.file.type === FileType.Video

  if (!isMedia) return false
  if (props.hasPrivilegedRole) return true
  return !props.isPrivate
})
</script>
